import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { 
    ArrowLeft, ArrowRight, UserPlus, Upload, FileText, CheckCircle2, 
    Loader2, Stethoscope, Activity, Heart, Truck, Shield, AlertTriangle,
    Check
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const professionTypes = [
    { id: 'doctor', name: 'Doctor', icon: Stethoscope, description: 'Licensed medical doctor' },
    { id: 'nurse', name: 'Nurse', icon: Heart, description: 'Registered nurse' },
    { id: 'hca', name: 'Healthcare Assistant', icon: Activity, description: 'Healthcare assistant' },
    { id: 'physio', name: 'Physiotherapist', icon: Activity, description: 'Licensed physiotherapist' },
    { id: 'logistics', name: 'Logistics', icon: Truck, description: 'Driver, companion, cleaner' }
];

const regulatoryBodies = [
    'General Medical Council (GMC)',
    'Nursing and Midwifery Council (NMC)',
    'Health and Care Professions Council (HCPC)',
    'General Dental Council (GDC)',
    'General Pharmaceutical Council (GPhC)',
    'Irish Medical Council',
    'NMBI (Ireland)',
    'State Medical Board (US)',
    'State Nursing Board (US)',
    'Other'
];

const specialties = [
    'General Practice',
    'Emergency Medicine',
    'Internal Medicine',
    'Pediatrics',
    'Surgery',
    'Obstetrics & Gynecology',
    'Psychiatry',
    'Cardiology',
    'Dermatology',
    'Orthopedics',
    'Neurology',
    'Oncology',
    'General Nursing',
    'Critical Care Nursing',
    'Pediatric Nursing',
    'Mental Health Nursing',
    'Community Nursing',
    'Physiotherapy',
    'Occupational Therapy',
    'Home Care',
    'Patient Transport',
    'Medical Courier',
    'Cleaning Services',
    'Companion Care',
    'Other'
];

export default function MediProviderRegister() {
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [uploadingDoc, setUploadingDoc] = useState(null);
    
    // Form data - removed subscription_plan
    const [formData, setFormData] = useState({
        // Account info
        email: '',
        password: '',
        confirmPassword: '',
        name: '',
        phone: '',
        
        // Professional info
        profession: '',
        registration_number: '',
        regulatory_body: '',
        specialty: '',
        travel_distance_km: '',
        
        // Documents (base64)
        government_id: null,
        government_id_name: '',
        registration_certificate: null,
        registration_certificate_name: '',
        cv: null,
        cv_name: '',
        additional_certificates: [],
        
        // Referees
        referee1_name: '',
        referee1_email: '',
        referee1_phone: '',
        referee1_relationship: '',
        referee2_name: '',
        referee2_email: '',
        referee2_phone: '',
        referee2_relationship: ''
    });

    const handleFileUpload = async (e, fieldName) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Please upload a JPG, PNG, WebP, or PDF file');
            return;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setUploadingDoc(fieldName);
        
        try {
            const reader = new FileReader();
            reader.onloadend = () => {
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: reader.result,
                    [`${fieldName}_name`]: file.name
                }));
                setUploadingDoc(null);
                toast.success(`${file.name} uploaded successfully`);
            };
            reader.readAsDataURL(file);
        } catch (err) {
            setUploadingDoc(null);
            toast.error('Failed to upload file');
        }
    };

    const validateStep = (stepNum) => {
        if (stepNum === 1) {
            if (!formData.email || !formData.password || !formData.name) {
                toast.error('Please fill in all required fields');
                return false;
            }
            if (formData.password !== formData.confirmPassword) {
                toast.error('Passwords do not match');
                return false;
            }
            if (formData.password.length < 6) {
                toast.error('Password must be at least 6 characters');
                return false;
            }
        }
        if (stepNum === 2) {
            if (!formData.profession || !formData.registration_number || !formData.regulatory_body) {
                toast.error('Please fill in all required professional details');
                return false;
            }
        }
        if (stepNum === 3) {
            if (!formData.government_id || !formData.registration_certificate || !formData.cv) {
                toast.error('Please upload all required documents (ID, Certificate, and CV)');
                return false;
            }
        }
        if (stepNum === 4) {
            if (!formData.referee1_name || !formData.referee1_email || !formData.referee2_name || !formData.referee2_email) {
                toast.error('Please provide both referees with name and email');
                return false;
            }
        }
        return true;
    };

    const handleNext = () => {
        if (validateStep(step)) {
            setStep(step + 1);
        }
    };

    const handleSubmit = async () => {
        if (!validateStep(4)) return;

        setLoading(true);
        try {
            // Register MediProvider (no payment required)
            const response = await axios.post(`${API}/mediprovider/register`, {
                email: formData.email,
                password: formData.password,
                name: formData.name,
                phone: formData.phone,
                profession: formData.profession,
                registration_number: formData.registration_number,
                regulatory_body: formData.regulatory_body,
                specialty: formData.specialty,
                travel_distance_km: parseInt(formData.travel_distance_km) || 50,
                government_id: formData.government_id,
                government_id_name: formData.government_id_name,
                registration_certificate: formData.registration_certificate,
                registration_certificate_name: formData.registration_certificate_name
            });

            // Store token and redirect to dashboard
            if (response.data.token) {
                localStorage.setItem('provider_token', response.data.token);
                localStorage.setItem('provider_user', JSON.stringify({
                    id: response.data.provider_id,
                    email: formData.email,
                    name: formData.name
                }));
                toast.success('Registration successful! Your documents are pending review.');
                navigate('/mediprovider/dashboard');
            } else {
                toast.success('Registration submitted! Please log in to continue.');
                navigate('/mediprovider/login');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Registration failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-slate-900 dark:to-slate-800 py-8 lg:py-12">
            <div className="max-w-2xl mx-auto px-4">
                <Link to="/">
                    <Button variant="ghost" className="mb-6">
                        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
                    </Button>
                </Link>

                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg">
                            <Stethoscope className="w-7 h-7 text-white" />
                        </div>
                    </div>
                    <h1 className="font-heading text-3xl font-bold mb-2">Join as MediProvider</h1>
                    <p className="text-muted-foreground">Provide healthcare services through MediGuide</p>
                </div>

                {/* Progress Steps */}
                <div className="flex items-center justify-center gap-2 mb-8">
                    {[1, 2, 3, 4, 5].map((s) => (
                        <div key={s} className="flex items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
                                ${step >= s ? 'bg-teal-500 text-white' : 'bg-slate-200 dark:bg-slate-700 text-muted-foreground'}`}>
                                {step > s ? <Check className="w-4 h-4" /> : s}
                            </div>
                            {s < 5 && <div className={`w-6 h-1 ${step > s ? 'bg-teal-500' : 'bg-slate-200 dark:bg-slate-700'}`} />}
                        </div>
                    ))}
                </div>

                <Card className="border-teal-200 dark:border-teal-800">
                    {/* Step 1: Account Info */}
                    {step === 1 && (
                        <>
                            <CardHeader>
                                <CardTitle>Account Information</CardTitle>
                                <CardDescription>Create your MediProvider account</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Full Name *</Label>
                                    <Input 
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        placeholder="Dr. John Smith"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Email *</Label>
                                    <Input 
                                        type="email"
                                        value={formData.email}
                                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                                        placeholder="john.smith@email.com"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Phone Number</Label>
                                    <Input 
                                        value={formData.phone}
                                        onChange={(e) => setFormData({...formData, phone: e.target.value})}
                                        placeholder="+44 7123 456789"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Password *</Label>
                                        <Input 
                                            type="password"
                                            value={formData.password}
                                            onChange={(e) => setFormData({...formData, password: e.target.value})}
                                            placeholder="••••••••"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Confirm Password *</Label>
                                        <Input 
                                            type="password"
                                            value={formData.confirmPassword}
                                            onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                                            placeholder="••••••••"
                                        />
                                    </div>
                                </div>
                            </CardContent>
                        </>
                    )}

                    {/* Step 2: Professional Info */}
                    {step === 2 && (
                        <>
                            <CardHeader>
                                <CardTitle>Professional Details</CardTitle>
                                <CardDescription>Tell us about your qualifications</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="space-y-3">
                                    <Label>Profession Type *</Label>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                        {professionTypes.map((type) => {
                                            const Icon = type.icon;
                                            const isSelected = formData.profession === type.id;
                                            return (
                                                <button
                                                    key={type.id}
                                                    type="button"
                                                    onClick={() => setFormData({...formData, profession: type.id, subscription_plan: ''})}
                                                    className={`p-3 rounded-xl border-2 text-left transition-all
                                                        ${isSelected 
                                                            ? 'border-teal-500 bg-teal-50 dark:bg-teal-950/30' 
                                                            : 'border-slate-200 dark:border-slate-700 hover:border-teal-300'}`}
                                                >
                                                    <Icon className={`w-5 h-5 mb-1 ${isSelected ? 'text-teal-500' : 'text-muted-foreground'}`} />
                                                    <p className="font-medium text-sm">{type.name}</p>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Professional Registration Number *</Label>
                                    <Input 
                                        value={formData.registration_number}
                                        onChange={(e) => setFormData({...formData, registration_number: e.target.value})}
                                        placeholder="e.g., GMC 1234567"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Regulatory Body *</Label>
                                    <Select value={formData.regulatory_body} onValueChange={(v) => setFormData({...formData, regulatory_body: v})}>
                                        <SelectTrigger><SelectValue placeholder="Select regulatory body" /></SelectTrigger>
                                        <SelectContent>
                                            {regulatoryBodies.map(body => (
                                                <SelectItem key={body} value={body}>{body}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Specialty</Label>
                                    <Select value={formData.specialty} onValueChange={(v) => setFormData({...formData, specialty: v})}>
                                        <SelectTrigger><SelectValue placeholder="Select specialty" /></SelectTrigger>
                                        <SelectContent>
                                            {specialties.map(spec => (
                                                <SelectItem key={spec} value={spec}>{spec}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Maximum Travel Distance (km)</Label>
                                    <Input 
                                        type="number"
                                        value={formData.travel_distance_km}
                                        onChange={(e) => setFormData({...formData, travel_distance_km: e.target.value})}
                                        placeholder="50"
                                    />
                                    <p className="text-xs text-muted-foreground">For home visits and call-outs</p>
                                </div>
                            </CardContent>
                        </>
                    )}

                    {/* Step 3: Document Upload */}
                    {step === 3 && (
                        <>
                            <CardHeader>
                                <CardTitle>Document Verification</CardTitle>
                                <CardDescription>Upload required documents for verification</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800">
                                    <div className="flex items-start gap-3">
                                        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-amber-800 dark:text-amber-200">
                                            <p className="font-medium mb-1">Important</p>
                                            <ul className="space-y-1 text-amber-700 dark:text-amber-300">
                                                <li>• Documents will be reviewed by our admin team</li>
                                                <li>• Your account will be in Pending status until approved</li>
                                                <li>• Ensure all documents are valid and clearly visible</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                {/* Government ID */}
                                <div className="space-y-2">
                                    <Label>Government-Issued Photo ID *</Label>
                                    <p className="text-xs text-muted-foreground mb-2">Passport, Driver License, or National ID</p>
                                    <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all
                                        ${formData.government_id ? 'border-teal-500 bg-teal-50/50 dark:bg-teal-950/20' : 'border-slate-300 dark:border-slate-600'}`}>
                                        {formData.government_id ? (
                                            <div className="flex items-center justify-center gap-3">
                                                <CheckCircle2 className="w-6 h-6 text-teal-500" />
                                                <div>
                                                    <p className="font-medium text-teal-700 dark:text-teal-300">{formData.government_id_name}</p>
                                                    <button 
                                                        className="text-xs text-muted-foreground hover:text-destructive"
                                                        onClick={() => setFormData({...formData, government_id: null, government_id_name: ''})}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <label className="cursor-pointer">
                                                <input 
                                                    type="file" 
                                                    className="hidden" 
                                                    accept="image/*,.pdf"
                                                    onChange={(e) => handleFileUpload(e, 'government_id')}
                                                />
                                                {uploadingDoc === 'government_id' ? (
                                                    <Loader2 className="w-8 h-8 mx-auto mb-2 text-teal-500 animate-spin" />
                                                ) : (
                                                    <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                                                )}
                                                <p className="text-sm text-muted-foreground">Click to upload</p>
                                                <p className="text-xs text-muted-foreground">JPG, PNG, WebP, or PDF (max 10MB)</p>
                                            </label>
                                        )}
                                    </div>
                                </div>

                                {/* Registration Certificate */}
                                <div className="space-y-2">
                                    <Label>Professional Registration Certificate *</Label>
                                    <p className="text-xs text-muted-foreground mb-2">Current and valid certificate from your regulatory body</p>
                                    <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all
                                        ${formData.registration_certificate ? 'border-teal-500 bg-teal-50/50 dark:bg-teal-950/20' : 'border-slate-300 dark:border-slate-600'}`}>
                                        {formData.registration_certificate ? (
                                            <div className="flex items-center justify-center gap-3">
                                                <CheckCircle2 className="w-6 h-6 text-teal-500" />
                                                <div>
                                                    <p className="font-medium text-teal-700 dark:text-teal-300">{formData.registration_certificate_name}</p>
                                                    <button 
                                                        className="text-xs text-muted-foreground hover:text-destructive"
                                                        onClick={() => setFormData({...formData, registration_certificate: null, registration_certificate_name: ''})}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <label className="cursor-pointer">
                                                <input 
                                                    type="file" 
                                                    className="hidden" 
                                                    accept="image/*,.pdf"
                                                    onChange={(e) => handleFileUpload(e, 'registration_certificate')}
                                                />
                                                {uploadingDoc === 'registration_certificate' ? (
                                                    <Loader2 className="w-8 h-8 mx-auto mb-2 text-teal-500 animate-spin" />
                                                ) : (
                                                    <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                                                )}
                                                <p className="text-sm text-muted-foreground">Click to upload</p>
                                                <p className="text-xs text-muted-foreground">JPG, PNG, WebP, or PDF (max 10MB)</p>
                                            </label>
                                        )}
                                    </div>
                                </div>

                                {/* CV Upload */}
                                <div className="space-y-2">
                                    <Label>CV / Resume *</Label>
                                    <p className="text-xs text-muted-foreground mb-2">Your professional curriculum vitae</p>
                                    <div className={`border-2 border-dashed rounded-xl p-6 text-center transition-all
                                        ${formData.cv ? 'border-teal-500 bg-teal-50/50 dark:bg-teal-950/20' : 'border-slate-300 dark:border-slate-600'}`}>
                                        {formData.cv ? (
                                            <div className="flex items-center justify-center gap-3">
                                                <CheckCircle2 className="w-6 h-6 text-teal-500" />
                                                <div>
                                                    <p className="font-medium text-teal-700 dark:text-teal-300">{formData.cv_name}</p>
                                                    <button 
                                                        className="text-xs text-muted-foreground hover:text-destructive"
                                                        onClick={() => setFormData({...formData, cv: null, cv_name: ''})}
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <label className="cursor-pointer">
                                                <input 
                                                    type="file" 
                                                    className="hidden" 
                                                    accept="image/*,.pdf,.doc,.docx"
                                                    onChange={(e) => handleFileUpload(e, 'cv')}
                                                />
                                                {uploadingDoc === 'cv' ? (
                                                    <Loader2 className="w-8 h-8 mx-auto mb-2 text-teal-500 animate-spin" />
                                                ) : (
                                                    <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
                                                )}
                                                <p className="text-sm text-muted-foreground">Click to upload</p>
                                                <p className="text-xs text-muted-foreground">PDF, DOC, or DOCX (max 10MB)</p>
                                            </label>
                                        )}
                                    </div>
                                </div>

                                {/* Additional Certificates */}
                                <div className="space-y-2">
                                    <Label>Additional Certificates (Optional)</Label>
                                    <p className="text-xs text-muted-foreground mb-2">Any other relevant qualifications or training certificates</p>
                                    <div className="border-2 border-dashed rounded-xl p-6 text-center border-slate-300 dark:border-slate-600">
                                        {formData.additional_certificates.length > 0 && (
                                            <div className="mb-4 space-y-2">
                                                {formData.additional_certificates.map((cert, idx) => (
                                                    <div key={idx} className="flex items-center justify-between bg-teal-50 dark:bg-teal-950/20 p-2 rounded-lg">
                                                        <span className="text-sm text-teal-700 dark:text-teal-300">{cert.name}</span>
                                                        <button 
                                                            className="text-xs text-destructive hover:underline"
                                                            onClick={() => {
                                                                const newCerts = [...formData.additional_certificates];
                                                                newCerts.splice(idx, 1);
                                                                setFormData({...formData, additional_certificates: newCerts});
                                                            }}
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        <label className="cursor-pointer">
                                            <input 
                                                type="file" 
                                                className="hidden" 
                                                accept="image/*,.pdf"
                                                onChange={(e) => {
                                                    const file = e.target.files[0];
                                                    if (!file) return;
                                                    const reader = new FileReader();
                                                    reader.onloadend = () => {
                                                        setFormData({
                                                            ...formData, 
                                                            additional_certificates: [
                                                                ...formData.additional_certificates,
                                                                { data: reader.result, name: file.name }
                                                            ]
                                                        });
                                                        toast.success(`${file.name} added`);
                                                    };
                                                    reader.readAsDataURL(file);
                                                }}
                                            />
                                            <Upload className="w-6 h-6 mx-auto mb-2 text-muted-foreground" />
                                            <p className="text-sm text-muted-foreground">Click to add more certificates</p>
                                        </label>
                                    </div>
                                </div>
                            </CardContent>
                        </>
                    )}

                    {/* Step 4: Referees */}
                    {step === 4 && (
                        <>
                            <CardHeader>
                                <CardTitle>Professional References</CardTitle>
                                <CardDescription>Provide two professional referees</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                {/* Referee 1 */}
                                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl space-y-4">
                                    <h4 className="font-medium">Referee 1 *</h4>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Full Name</Label>
                                            <Input 
                                                value={formData.referee1_name}
                                                onChange={(e) => setFormData({...formData, referee1_name: e.target.value})}
                                                placeholder="Dr. Jane Smith"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Relationship</Label>
                                            <Input 
                                                value={formData.referee1_relationship}
                                                onChange={(e) => setFormData({...formData, referee1_relationship: e.target.value})}
                                                placeholder="e.g., Former Supervisor"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Email</Label>
                                            <Input 
                                                type="email"
                                                value={formData.referee1_email}
                                                onChange={(e) => setFormData({...formData, referee1_email: e.target.value})}
                                                placeholder="jane.smith@hospital.com"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Phone</Label>
                                            <Input 
                                                value={formData.referee1_phone}
                                                onChange={(e) => setFormData({...formData, referee1_phone: e.target.value})}
                                                placeholder="+44 7123 456789"
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Referee 2 */}
                                <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl space-y-4">
                                    <h4 className="font-medium">Referee 2 *</h4>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Full Name</Label>
                                            <Input 
                                                value={formData.referee2_name}
                                                onChange={(e) => setFormData({...formData, referee2_name: e.target.value})}
                                                placeholder="Prof. John Doe"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Relationship</Label>
                                            <Input 
                                                value={formData.referee2_relationship}
                                                onChange={(e) => setFormData({...formData, referee2_relationship: e.target.value})}
                                                placeholder="e.g., Colleague"
                                            />
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label>Email</Label>
                                            <Input 
                                                type="email"
                                                value={formData.referee2_email}
                                                onChange={(e) => setFormData({...formData, referee2_email: e.target.value})}
                                                placeholder="john.doe@clinic.com"
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Phone</Label>
                                            <Input 
                                                value={formData.referee2_phone}
                                                onChange={(e) => setFormData({...formData, referee2_phone: e.target.value})}
                                                placeholder="+44 7123 456789"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </>
                    )}

                    {/* Step 5: Review & Submit */}
                    {step === 5 && (
                        <>
                            <CardHeader>
                                <CardTitle>Review & Submit</CardTitle>
                                <CardDescription>Please review your information before submitting</CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-4">
                                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                                        <p className="text-xs text-muted-foreground mb-1">Account</p>
                                        <p className="font-medium">{formData.name}</p>
                                        <p className="text-sm text-muted-foreground">{formData.email}</p>
                                    </div>

                                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                                        <p className="text-xs text-muted-foreground mb-1">Professional Details</p>
                                        <p className="font-medium capitalize">{formData.profession}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {formData.registration_number} • {formData.regulatory_body}
                                        </p>
                                        {formData.specialty && (
                                            <p className="text-sm text-muted-foreground">Specialty: {formData.specialty}</p>
                                        )}
                                    </div>

                                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-xl">
                                        <p className="text-xs text-muted-foreground mb-1">Documents</p>
                                        <div className="flex items-center gap-2 text-sm">
                                            <CheckCircle2 className="w-4 h-4 text-teal-500" />
                                            <span>Government ID: {formData.government_id_name}</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-sm mt-1">
                                            <CheckCircle2 className="w-4 h-4 text-teal-500" />
                                            <span>Certificate: {formData.registration_certificate_name}</span>
                                        </div>
                                    </div>

                                    {/* Trial Info instead of payment */}
                                    <div className="p-4 bg-teal-50 dark:bg-teal-950/30 rounded-xl border border-teal-200 dark:border-teal-800">
                                        <p className="text-xs text-muted-foreground mb-1">Your Trial</p>
                                        <p className="font-medium text-teal-700 dark:text-teal-300">
                                            3 Free Jobs Trial
                                        </p>
                                        <p className="text-sm text-teal-600 dark:text-teal-400 mt-1">
                                            Complete 3 jobs with 20% commission to experience the platform before choosing a subscription plan.
                                        </p>
                                    </div>
                                </div>

                                <div className="p-4 bg-amber-50 dark:bg-amber-950/30 rounded-xl border border-amber-200 dark:border-amber-800">
                                    <div className="flex items-start gap-3">
                                        <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-amber-800 dark:text-amber-200">
                                            <p className="font-medium mb-1">What happens next?</p>
                                            <ol className="list-decimal list-inside space-y-1 text-amber-700 dark:text-amber-300">
                                                <li>Your documents will be reviewed (1-3 business days)</li>
                                                <li>Once approved, your folder turns green</li>
                                                <li>Start receiving job requests!</li>
                                                <li>After 3 jobs, choose a subscription plan</li>
                                            </ol>
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </>
                    )}

                    {/* Navigation Buttons */}
                    <div className="px-6 pb-6 flex justify-between">
                        {step > 1 ? (
                            <Button variant="outline" onClick={() => setStep(step - 1)}>
                                <ArrowLeft className="w-4 h-4 mr-2" /> Back
                            </Button>
                        ) : (
                            <div />
                        )}
                        
                        {step < 5 ? (
                            <Button onClick={handleNext} className="bg-teal-500 hover:bg-teal-600">
                                Next <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        ) : (
                            <Button onClick={handleSubmit} disabled={loading} className="bg-teal-500 hover:bg-teal-600">
                                {loading ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <CheckCircle2 className="w-4 h-4 mr-2" />
                                )}
                                Complete Registration
                            </Button>
                        )}
                    </div>
                </Card>

                <p className="text-center text-sm text-muted-foreground mt-6">
                    Already have an account?{' '}
                    <Link to="/mediprovider/login" className="text-teal-600 hover:underline">
                        Sign in
                    </Link>
                </p>
            </div>
        </div>
    );
}
